import { SearchOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { sortBy } from 'lodash';
import { type BaseSelectRef } from 'rc-select';
import { type MouseEvent as ReactMouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { filterOption } from 'components/choosers/helpers';
import { CustomFilterContainer, CustomFilterPopover } from 'components/data/helpers/filters.style';
import { Button } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { useGetSitesMinimalQuery } from 'types/atlas-graphql';

import { LockedFilterDropdown } from './LockedFilterDropdown';
import {
  FilterMultiSelectContainer,
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from './TableFilterMultiSelect';

type SiteFilterProps = CustomFilterDropdownProps & {
  lockedSiteIds?: string[];
};

export const SiteFilter: React.FunctionComponent<SiteFilterProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  lockedSiteIds,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<BaseSelectRef>(null);
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');

  const { data: sitesData, loading: sitesLoading } = useGetSitesMinimalQuery();
  const sites = sortBy(sitesData?.sites?.items, ['name']) ?? [];

  const handleConfirm = () => {
    setSearchValue('');
    confirm({ closeDropdown: true });
  };

  // clearFilters only resets the draft; confirm applies it.
  const handleReset = () => {
    setSearchValue('');
    clearFilters && clearFilters();
    confirm({ closeDropdown: true });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
  };

  if (lockedSiteIds?.length) {
    return (
      <LockedFilterDropdown
        options={lockedSiteIds.map(id => ({
          value: id,
          label: sites.find(site => site.id === id)?.name ?? id,
        }))}
      />
    );
  }

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover className="ant-table-filter-dropdown">
        <CustomFilterContainer>
          <FilterMultiSelectContainer>
            <TableFilterMultiSelect
              id="site-filter-dropdown"
              ref={inputRef}
              placeholder={t('table.selectSite', 'Select Site...')}
              loading={sitesLoading}
              allowClear
              showSearch
              filterOption={filterOption}
              virtual={false}
              value={sitesLoading ? undefined : (selectedKeys as string[]) ?? []}
              onChange={setSelectedKeys}
              searchValue={searchValue}
              onSearch={setSearchValue}
              onOpenChange={handleOpenChange}
              suffixIcon={<SearchOutlined aria-hidden />}
            >
              {sites.map(site => (
                <Select.Option key={site.id} className="site-filter-option" value={site.id}>
                  {site.name}
                </Select.Option>
              ))}
            </TableFilterMultiSelect>
          </FilterMultiSelectContainer>
        </CustomFilterContainer>
        <div className="ant-table-filter-dropdown-btns" style={{ borderTop: '1px solid #f0f0f0' }}>
          <Button
            type="link"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleReset)
            }
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleConfirm)
            }
          >
            {t('table.ok', 'OK')}
          </Button>
        </div>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
